/**
 * Tiny fetch wrapper for the Go client API.
 *
 * - Always includes credentials (we use httpOnly session cookie).
 * - Adds the X-CSRF-Token header from the `gc_csrf` cookie on unsafe methods.
 * - Throws ApiError with status + parsed body on non-2xx responses.
 *
 * Path conventions: pass paths starting with /v1/... — apiBaseUrl is "" in
 * prod (same-origin) and dev (vite proxy forwards /v1 → :8080).
 */

import { env } from "./env";
import { isMockEnabled, mockApiFetch } from "./mock";

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  /** Plain JS value — will be JSON-encoded. Prefer over `body`. */
  json?: unknown;
  /** Raw fetch body (for uploads). Mutually exclusive with `json`. */
  body?: BodyInit;
}

export async function apiFetch<TResponse = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  // Mock mode short-circuits before any network call. See lib/mock.ts.
  if (isMockEnabled) {
    return mockApiFetch<TResponse>(path, {
      method: options.method,
      json: options.json,
    });
  }

  const { json, body, headers, method = "GET", ...rest } = options;

  const finalHeaders = new Headers(headers);
  if (json !== undefined) {
    finalHeaders.set("Content-Type", "application/json");
  }

  // CSRF — double-submit pattern. Server sets `gc_csrf` cookie on session
  // creation (see services/api/internal/auth/csrf.go::CSRFCookie); client
  // echoes it on unsafe methods. Cookie name uses the gc_ prefix shared by
  // gc_session and gc_oauth_*.
  if (UNSAFE_METHODS.has(method.toUpperCase())) {
    const csrf = readCookie("gc_csrf");
    if (csrf) finalHeaders.set("X-CSRF-Token", csrf);
  }

  const url = path.startsWith("http") ? path : `${env.apiBaseUrl}${path}`;

  const resp = await fetch(url, {
    ...rest,
    method,
    credentials: "include",
    headers: finalHeaders,
    body: json !== undefined ? JSON.stringify(json) : body,
  });

  // No content
  if (resp.status === 204) return undefined as TResponse;

  const contentType = resp.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const parsed = isJson ? await resp.json().catch(() => null) : await resp.text();

  if (!resp.ok) {
    const message =
      typeof parsed === "string"
        ? parsed
        : (parsed as { message?: string })?.message ?? resp.statusText;
    throw new ApiError(resp.status, message, parsed);
  }

  return parsed as TResponse;
}
